import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {change, Field, reduxForm} from 'redux-form';
import {connect} from 'react-redux';
import {compose} from 'recompose';
import axios from 'axios/index';
import {clearUser} from '../../authClient';
import {email, translate} from 'admin-on-rest';
import Config from '../messages';
import Emitter from '../shared/eventEmitter';
import {isLocalhost, removeSearchQuery} from '../shared/serverFunctions';
import {
    checkTokenDates,
    createCodeVerifier,
    decodeJWTToken,
    generateCodeChallenge,
    getKeycloakAuthUrl,
    getKeycloakTokenUrl,
    makeDatesForToken
} from '../shared/tokenFunctions';
import FuseTheme from '../global/theme';
import TextField from 'material-ui/TextField';
import RaisedButton from 'material-ui/RaisedButton';
import Dialog from 'material-ui/Dialog';
import Snackbar from 'material-ui/Snackbar';

/**
 * Login Input fields
 * http://redux-form.com/6.4.3/examples/material-ui/
 *
 * @param touched
 * @param error
 * @param inputProps
 * @param props
 * @return {*}
 */
const renderInput = ({meta: {touched, error} = {}, input: {...inputProps}, ...props}) =>
    <TextField
        errorText={touched && error}
        {...inputProps}
        {...props}
        fullWidth
    />;

/**
 * A component that checks token status and displays a modal with login form
 *
 */
class TokenCheck extends Component {
    constructor(props) {
        super(props);
        // If TokenCheck is loaded from a Keycloak redirect, process and set
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        this.state = {
            expired: false,
            modalMessage: 'Please Sign In.',
            notificationDelay: 3000,
            notificationMessage: '',
            notificationOpen: false,
            notificationType: 'error',
            open: false,
            submitting: false,
            token: null,
            tokenState: '',
            useKeycloak: !!(code)
        };
    }

    componentDidMount() {
        Emitter.on('token_state', () => this.processToken(null));
        if (this.state.useKeycloak) {
            // If the Login page loaded from a Keycloak redirect, fetch token
            this.postKeycloakLoginFromUrl();
        } else {
            let token = null;
            const propsToken = this.props.token;
            if (propsToken) {
                // Process token if provided
                token = propsToken;
                this.setState({
                    token: propsToken
                });
            }
            this.processToken(token);
        }
    }

    componentWillReceiveProps(nextProps) {
        if (
            nextProps.token !== this.props.token ||
            nextProps.tokenState !== this.props.tokenState ||
            nextProps.location !== this.props.location
        ) {
            // Process new token value
            this.setState({
                    location: nextProps.location,
                    token: nextProps.token,
                    tokenState: nextProps.tokenState
                }, () => this.processToken(nextProps.token)
            );
        }
    }

    componentWillUnmount() {
        Emitter.off('token_status', () => this.processToken(null));
    }

    /**
     * Redirect to a requested path
     *
     * @param location
     */
    redirect(location) {
        window.location.href = location;
    }

    /**
     * Post to Keycloak with code, then check for exchange token
     */
    postKeycloakLoginFromUrl = () => {
        localStorage.removeItem('kc_id_token');
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        if (code) {
            removeSearchQuery();
            let codeVerifier = sessionStorage.getItem('code_verifier');
            if (!codeVerifier) {
                codeVerifier = localStorage.getItem('code_verifier');
                sessionStorage.setItem('code_verifier', codeVerifier);
            }
            let redirect = localStorage.getItem('redirect');
            const params = new URLSearchParams({
                client_id: 'user',
                grant_type: 'authorization_code',
                code: code,
                redirect_uri: redirect,
                code_verifier: codeVerifier,
            });
            const tokenUrl = getKeycloakTokenUrl();
            axios.post(tokenUrl, params, {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded'
                }
            }).then((response) => {
                if (response.data && response.data.access_token) {
                    localStorage.setItem('kc_token', response.data.access_token);
                    localStorage.setItem('kc_id_token', response.data.id_token);
                    this.postExchangeToken();
                }
            }).catch((error) => {
                console.log('Keycloak login error: ', error);
            });
        }
    };

    /**
     * Post to KTX to exchange Keycloak token for a CloudSent token
     */
    postExchangeToken = () => {
        const endpoint = Config.fuse.api.EXCHANGE_ENDPOINT;
        const exchangeURL = (isLocalhost) ? Config.fuse.api.LOCAL_AUTH_URI + endpoint : endpoint;
        const exchangeParams = new URLSearchParams({
            jwt_token: localStorage.getItem('kc_token')
        });
        axios.post(exchangeURL, exchangeParams, {
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
            }
        }).then((response) => {
            if (response.data && response.data.token) {
                // Extract User from token
                const token = response.data.token;
                const decodedToken = decodeJWTToken(token);
                const user = (decodedToken.payload && decodedToken.payload.data) ? decodedToken.payload.data : null;
                if (user.locked === true) {
                    this.loginFailed('Login failed: This User is locked.');
                } else if (user.active === false) {
                    this.loginFailed('Login failed: This User is inactive.');
                } else {
                    this.loginSucceeded(user);
                    // Store the CloudSent token for subsequent calls
                    localStorage.setItem('token', token);
                    localStorage.removeItem('kc_token');
                    localStorage.setItem('auth_method', 'kc');
                    localStorage.removeItem('code_verifier');
                    // Load the TokenCheck
                    this.setState({
                        displayTokenCheck: true,
                        token: token
                    });
                }
            }
        }).catch((error) => {
            console.log('exchange token error: ', error);
        });
    };

    /**
     * Check a new or stored token for expired status
     *
     * @param token
     */
    processToken(token) {
        // Set or retrieve token
        const tokenValue = (token) ? token : localStorage.getItem('token');
        if (tokenValue) {
            let expired = false;
            let open = false;
            let modalMessage = 'Please Sign In.';

            // Decode the stored token
            const decodedToken = decodeJWTToken(tokenValue);
            const exp = (decodedToken.payload && decodedToken.payload.exp) ? decodedToken.payload.exp : 0;
            if (exp) {
                let tokenStatus = checkTokenDates(exp);
                const tokenState = this.state.tokenState;
                if (tokenStatus.expired || tokenState === 'expired') {
                    // Expiration date has passed
                    expired = true;
                    modalMessage = 'Your Admin session has expired. Please sign in to continue.';
                    open = true;
                } else if (tokenStatus.withinWarning || tokenState === 'warning') {
                    // Within 10-minute warning time
                    modalMessage = 'Your Admin session is nearly expired. Please re-authenticate to continue your session.';
                    open = true;
                } else {
                    // Set timeout for expiration date warning
                    this.setTimeouts(exp);
                }
            }
            this.setState({
                expired: expired,
                modalMessage: modalMessage,
                open: open,
                token: tokenValue,
            });
            this.props.dispatch(change('tokenForm', 'token', tokenValue));
        }
    }

    /**
     * Create timeouts to display warning and expired modals
     *
     * @param exp
     */
    setTimeouts(exp) {
        const MAX_TIMEOUT = 1728000000;

        const dates = makeDatesForToken(exp);
        // Duration is 10 minutes before expiration
        let warningDuration = dates.warningDate - dates.nowDate;
        let expiredDuration = dates.expDate - dates.nowDate;
        const safeWarning = Math.min(warningDuration, MAX_TIMEOUT);
        const safeExpired = Math.min(expiredDuration, MAX_TIMEOUT);

        setTimeout(() => {
            this.setState({
                modalMessage: 'Your session is nearly expired. Please re-authenticate to continue your session.',
                open: true
            });
        }, safeWarning);
        setTimeout(() => {
            this.setState({
                expired: true,
                modalMessage: 'Your session has expired. Please re-authenticate to resume your session.',
                open: true
            });
        }, safeExpired);
    }

    /**
     * Handle initial login from form
     *
     * @param email
     * @param password
     */
    postCloudSentLogin = ({email, password}) => {
        // Set Authorization URI to relative unless developing locally
        const endpoint = Config.fuse.api.AUTH_ENDPOINT;
        const uri = (isLocalhost) ? Config.fuse.api.LOCAL_AUTH_URI + endpoint : endpoint;
        const payload = {
            "payload": {
                "systemUserDataModel": {
                    email: email,
                    password: password
                }
            }
        };
        this.setState({
            submitting: true
        });
        localStorage.removeItem('token');
        localStorage.removeItem('kc_id_token');

        // Retrieve the data and assign results to the Select choices
        axios
            .post(uri, payload)
            .then((result) => {
                if (result.data.payload) {
                    // Check the token and user object if available
                    const token = result.data.payload.token;
                    const user = result.data.payload.user;
                    if (token && user) {
                        this.loginSucceeded();
                        // Store the token for all subsequent calls and process its expiration
                        localStorage.setItem('token', token);
                        localStorage.setItem('auth_method', 'cs');
                        this.processToken(token);
                    } else {
                        this.loginFailed('Login failed: Incorrect credentials.');
                    }
                } else {
                    this.loginFailed('Login failed: No token available');
                }
                this.setState({
                    submitting: false
                });
            })
            .catch((error) => {
                if (error.response) {
                    // Server response outside 2xx
                    let payload = error.response.data.payload;
                    let message = error.response.data.message;
                    let error_message = error.response.data.error_message;

                    if (payload && payload.indexOf('password') !== -1) {
                        this.loginFailed('Incorrect credentials.');
                    } else if (message) {
                        this.loginFailed(message);
                    } else if (error_message) {
                        this.loginFailed(error_message);
                    } else {
                        this.loginFailed('Login failed.');
                    }
                } else if (error.request) {
                    // No response
                    console.log('Request Error: ' + error.request);
                    this.loginFailed('No response from server.');
                } else {
                    console.log('Error: ', error.message);
                    this.loginFailed('No response from server.');
                }
                this.setState({
                    submitting: false
                });
            });
    };

    /**
     * Generate a challenge from Keycloak Login click
     */
    handleKeycloakLoginClick = () => {
        const codeVerifier = createCodeVerifier();
        localStorage.removeItem('token');
        localStorage.removeItem('kc_id_token');
        generateCodeChallenge(codeVerifier).then((codeChallenge) => {
            localStorage.setItem('redirect', window.location.href);
            window.location.href = getKeycloakAuthUrl(codeChallenge, false);
        });
    };

    /**
     * Display success notification
     */
    loginSucceeded = () => {
        sessionStorage.setItem('token_state', 'valid');
        this.setState({
            notificationMessage: 'Logged in successfully',
            notificationType: 'message',
            notificationOpen: true,
            open: false
        });
        this.setNotificationClose();
        let redirect = localStorage.getItem('redirect');
        if (redirect) {
            localStorage.removeItem('redirect');
            this.redirect(redirect);
        }
        setTimeout(() => {
            window.location.reload();
        }, 400);
    };

    /**
     * Display error notification
     */
    loginFailed = (message) => {
        sessionStorage.setItem('token_state', 'invalid');
        this.setState({
            notificationMessage: message,
            notificationType: 'error',
            notificationOpen: true
        });
        this.setNotificationClose();
    };

    handleOpen = () => {
        this.setState({open: true});
    };

    handleClose = () => {
        this.setState({open: false});
        // If user closed the dialog without logging in, force a login
        const isExpired = this.state.expired;
        if (isExpired) {
            clearUser();
            this.redirect('/#/login');
        }
    };

    setNotificationClose = () => {
        setTimeout(this.handleNotificationClose, this.state.notificationDelay);
    };

    handleNotificationClose = () => {
        this.setState({notificationOpen: false});
    };

    render() {
        const {
            handleSubmit,
        } = this.props;

        const {
            submitting
        } = this.state;

        const styles = {
            dialogContent: {maxWidth: 500},
            input: {display: 'flex'},
            form: {padding: '0 1em 1em 1em'},
            hint: {
                textAlign: 'center',
                marginTop: '1em',
                color: '#ccc'
            },
            logo: {
                width: 240,
                height: 125,
                margin: '1em auto'
            },
            logoImage: {width: '100%', height: 'auto'},
            tokenContainer: {boxSizing: 'border-box'},
        };
        const notificationStyle = {};
        if (this.state.notificationType === 'error') {
            notificationStyle.backgroundColor = FuseTheme.palette.accent1Color;
        }
        if (this.state.notificationType === 'message') {
            notificationStyle.backgroundColor = FuseTheme.palette.primary1Color;
        }

        return (
            <div className="token-container">
                <Dialog
                    modal={false}
                    open={this.state.open}
                    onRequestClose={this.handleClose}
                    contentStyle={styles.dialogContent}
                >
                    <div style={styles.dialogContent}>
                        <div style={styles.logo}>
                            <img src="CloudSent-POS-logo-admin.png" style={styles.logoImage} alt="CloudSent POS"/>
                        </div>
                        <form onSubmit={handleSubmit(this.postCloudSentLogin)}>
                            <div style={styles.form}>
                                <p style={styles.hint}>{this.state.modalMessage}</p>
                                <div style={styles.input}>
                                    <Field
                                        name="email"
                                        type="email"
                                        component={renderInput}
                                        floatingLabelText="Email"
                                        validate={[email]}
                                    />
                                </div>
                                <div style={styles.input}>
                                    <Field
                                        name="password"
                                        component={renderInput}
                                        floatingLabelText="Password"
                                        type="password"
                                    />
                                </div>
                                <RaisedButton
                                    type="submit"
                                    primary
                                    disabled={submitting}
                                    label="Sign In"
                                    fullWidth
                                />
                                <p style={styles.hint}>
                                    Or log in with CloudSent SSO:
                                </p>
                                <RaisedButton
                                    type="button"
                                    primary
                                    disabled={submitting}
                                    label="Login with CloudSent SSO"
                                    fullWidth
                                    onClick={this.handleKeycloakLoginClick}
                                />
                            </div>
                        </form>
                    </div>
                </Dialog>
                <Snackbar
                    open={this.state.notificationOpen}
                    message={this.state.notificationMessage}
                    autoHideDuration={this.state.notificationDelay}
                    onRequestClose={this.handleNotificationClose}
                    bodyStyle={notificationStyle}
                />
            </div>
        );
    }
}

const mapStateToProps = (state, ownProps) => {
    let form = state.form['tokenForm'];
    if (form && form.values) {
        const location = state.routing.location.pathname;
        const tokenValue = (form.values['token']) ? form.values['token'] : null;
        const tokenState = sessionStorage.getItem('token_state');
        return {
            location: location,
            token: tokenValue,
            tokenState: tokenState
        }
    }
    return {token: null}
};

TokenCheck.propTypes = {
    addField: PropTypes.bool,
    expired: PropTypes.bool,
    location: PropTypes.string,
    name: PropTypes.string,
    submitOnEnter: PropTypes.bool,
    token: PropTypes.any,
    tokenState: PropTypes.string,
    translate: PropTypes.func
};

TokenCheck.defaultProps = {
    addField: true,
    expired: false,
    name: "TokenCheck",
    submitOnEnter: false,
};

const enhance = compose(
    translate,
    reduxForm({
        form: 'tokenForm',
        enableReinitialize: true,
    }),
    connect(mapStateToProps)
);

export default enhance(TokenCheck);



// WEBPACK FOOTER //
// ./src/Fuse/components/TokenCheck.js